import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import WorkflowNode from './WorkflowNode';
import PlaceholderNode from './PlaceholderNode';
import CanvasContextMenu from './CanvasContextMenu';
import EmptyCanvasOverlay from './EmptyCanvasOverlay';
import { NODE_TYPES } from './nodeTypes';

const nodeTypes = { workflowNode: WorkflowNode, placeholderNode: PlaceholderNode };

const PLACEHOLDER_OFFSET_X = 60;

/**
 * Canvas Mode renderer for the Workflow Builder.
 *
 * All free-form positioning, drag/drop, edge-drop context menu, and
 * placeholder-add affordances live here. The view is data-driven: it
 * reads/writes through the `state` hook (useWorkflowState) and reports
 * selection / highlight state up via callbacks.
 */
export default function CanvasWorkflowView({
  state,
  defaultEdgeOptions,
  selectedNodeId,
  highlightedNodeId,
  activeRunId,
  activeRunNodeStatuses,
  onSelectNode,
  onClearSelection,
  onReactFlowInit,
}) {
  const {
    nodes,
    edges,
    nodeConfigs,
    onNodesChange,
    onEdgesChange,
    addNodeAt,
    replaceWithTemplate,
    replaceWithSingleTrigger,
    connectNodes,
  } = state;

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstanceState] = useState(null);
  const setReactFlowInstance = useCallback(
    (instance) => {
      setReactFlowInstanceState(instance);
      if (onReactFlowInit) onReactFlowInit(instance);
    },
    [onReactFlowInit]
  );

  // Edge-drop tracking: xyflow resets connectionState on drop
  const connectStartRef = useRef(null);
  const edgeDropRef = useRef(false);

  // Context menu state (right-click, edge drop, placeholder click)
  const [contextMenu, setContextMenu] = useState(null);

  // ------- xyflow connection handlers -------

  const onConnect = useCallback(
    (params) => connectNodes(params),
    [connectNodes]
  );

  const onConnectStart = useCallback((_, params) => {
    connectStartRef.current = params;
  }, []);

  const onConnectEnd = useCallback(
    (event, connectionState) => {
      const startParams = connectStartRef.current;
      connectStartRef.current = null;
      if (!reactFlowInstance || !startParams) return;
      if (connectionState.isValid) return;

      const { clientX, clientY } = event.changedTouches?.[0] ?? event;
      const flowPosition = reactFlowInstance.screenToFlowPosition({
        x: clientX,
        y: clientY,
      });
      edgeDropRef.current = true;

      setContextMenu({
        x: clientX,
        y: clientY,
        flowPosition,
        connectFrom: {
          nodeId: startParams.nodeId,
          handleId: startParams.handleId || null,
          handleType: startParams.handleType || 'source',
        },
      });
    },
    [reactFlowInstance]
  );

  // ------- Drag and drop from palette -------

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !NODE_TYPES[type]) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNodeAt({ typeKey: type, position });

      requestAnimationFrame(() => {
        reactFlowInstance?.setCenter(
          position.x + 90,
          position.y + 25,
          { duration: 300, zoom: reactFlowInstance.getZoom() }
        );
      });
    },
    [reactFlowInstance, addNodeAt]
  );

  // ------- Click handlers -------

  const onNodeClick = useCallback(
    (_, node) => {
      if (node.id.startsWith('_ph_')) return;
      onSelectNode(node.id);
    },
    [onSelectNode]
  );

  const onPaneClick = useCallback(() => {
    if (edgeDropRef.current) {
      edgeDropRef.current = false;
      return;
    }
    onClearSelection();
    setContextMenu(null);
  }, [onClearSelection]);

  const handleCanvasContextMenu = useCallback(
    (event) => {
      const target = event.target;
      const isPane =
        target.classList.contains('react-flow__pane') ||
        target.classList.contains('react-flow__background') ||
        target.closest('.react-flow__pane');
      if (!isPane || target.closest('.react-flow__node')) return;

      event.preventDefault();
      const flowPosition = reactFlowInstance?.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      setContextMenu({ x: event.clientX, y: event.clientY, flowPosition });
    },
    [reactFlowInstance]
  );

  const handleContextMenuAddNode = useCallback(
    (nodeTypeKey) => {
      if (!contextMenu?.flowPosition || !NODE_TYPES[nodeTypeKey]) return;

      const newId = addNodeAt({
        typeKey: nodeTypeKey,
        position: contextMenu.flowPosition,
        connectFrom: contextMenu.connectFrom || null,
      });
      const flowPos = contextMenu.flowPosition;
      setContextMenu(null);
      if (newId) onSelectNode(newId);

      requestAnimationFrame(() => {
        reactFlowInstance?.setCenter(
          flowPos.x + 90,
          flowPos.y + 25,
          { duration: 300, zoom: reactFlowInstance.getZoom() }
        );
      });
    },
    [contextMenu, addNodeAt, reactFlowInstance, onSelectNode]
  );

  // ------- Empty canvas affordances -------

  const handleAddTemplate = useCallback(
    (template) => {
      const result = replaceWithTemplate(template);
      if (!result) return;
      onSelectNode(result.triggerId);
      requestAnimationFrame(() => {
        reactFlowInstance?.fitView({ padding: 0.3, duration: 300 });
      });
    },
    [replaceWithTemplate, reactFlowInstance, onSelectNode]
  );

  const handleAddSingleTrigger = useCallback(
    (triggerType) => {
      const position = reactFlowInstance
        ? reactFlowInstance.screenToFlowPosition({
            x: reactFlowWrapper.current.getBoundingClientRect().width / 2,
            y: reactFlowWrapper.current.getBoundingClientRect().height / 2,
          })
        : { x: 250, y: 200 };

      const result = replaceWithSingleTrigger(triggerType, position);
      if (!result) return;
      onSelectNode(result.triggerId);
      requestAnimationFrame(() => {
        reactFlowInstance?.fitView({ padding: 0.3, duration: 300 });
      });
    },
    [reactFlowInstance, replaceWithSingleTrigger, onSelectNode]
  );

  // ------- Placeholder ("Add" pip) handling for the selected node's
  //         unconnected source handles. -------

  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const reactFlowInstanceRef = useRef(reactFlowInstance);
  reactFlowInstanceRef.current = reactFlowInstance;

  const handlePlaceholderClick = useCallback(
    (sourceNodeId, sourceHandleId) => {
      const instance = reactFlowInstanceRef.current;
      if (!instance) return;
      const sourceNode = nodesRef.current.find((n) => n.id === sourceNodeId);
      if (!sourceNode) return;

      const offsetX = 200;
      const flowPos = {
        x: sourceNode.position.x + offsetX + 60,
        y: sourceNode.position.y + 25,
      };
      const screenPos = instance.flowToScreenPosition(flowPos);

      setContextMenu({
        x: screenPos.x,
        y: screenPos.y,
        flowPosition: flowPos,
        connectFrom: {
          nodeId: sourceNodeId,
          handleId: sourceHandleId || null,
          handleType: 'source',
        },
      });
    },
    []
  );

  // ------- Derived render data -------

  // Decorate nodes with execution status overlays, router rule data, and
  // the (transient) "highlight" flag set when the user clicks a step in
  // the Explain panel.
  const nodesWithStatus = useMemo(() => {
    return nodes.map((node) => {
      let data = { ...node.data };

      if (node.data.nodeType === 'router') {
        const config = nodeConfigs[node.id] || {};
        data.routerRules = config.rules || [];
      }

      if (activeRunId && Object.keys(activeRunNodeStatuses).length > 0) {
        data.executionStatus = activeRunNodeStatuses[node.id] || null;
      }

      if (highlightedNodeId && node.id === highlightedNodeId) {
        data.highlight = true;
      }

      return { ...node, data };
    });
  }, [nodes, activeRunId, activeRunNodeStatuses, nodeConfigs, highlightedNodeId]);

  // "Add" placeholder pips on the selected node's unconnected outgoing handles.
  const { placeholderNodes, placeholderEdges } = useMemo(() => {
    const phNodes = [];
    const phEdges = [];

    if (!selectedNodeId) return { placeholderNodes: phNodes, placeholderEdges: phEdges };

    const node = nodes.find((n) => n.id === selectedNodeId);
    if (!node) return { placeholderNodes: phNodes, placeholderEdges: phEdges };

    const typeDef = NODE_TYPES[node.data.nodeType];
    if (!typeDef) return { placeholderNodes: phNodes, placeholderEdges: phEdges };

    const isTerminal = node.data.nodeType === 'stop';
    if (isTerminal) return { placeholderNodes: phNodes, placeholderEdges: phEdges };

    const connectedSources = new Set();
    for (const edge of edges) {
      if (edge.source === selectedNodeId) {
        connectedSources.add(edge.sourceHandle || '');
      }
    }

    const isConditional = node.data.nodeType === 'conditional';
    const isRouter = node.data.nodeType === 'router';

    if (isRouter) {
      const config = nodeConfigs[node.id] || {};
      const rules = config.rules || [];
      const handleIds = [...rules.map((_, idx) => `route-${idx}`), 'default'];
      const totalHandles = handleIds.length;

      handleIds.forEach((handleId, idx) => {
        if (connectedSources.has(handleId)) return;
        const pct = ((idx + 1) / (totalHandles + 1));
        const offsetY = (pct - 0.5) * 80;
        const phId = `_ph_${node.id}_${handleId}`;
        phNodes.push({
          id: phId,
          type: 'placeholderNode',
          position: {
            x: node.position.x + 160 + PLACEHOLDER_OFFSET_X,
            y: node.position.y + offsetY,
          },
          data: { onClick: () => handlePlaceholderClick(node.id, handleId) },
          selectable: false,
          draggable: false,
          focusable: false,
        });
        phEdges.push({
          id: `_phe_${node.id}_${handleId}`,
          source: node.id,
          sourceHandle: handleId,
          target: phId,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#e5e7eb', strokeWidth: 1, strokeDasharray: '6 4' },
        });
      });
    } else if (isConditional) {
      for (const handleId of ['true', 'false']) {
        if (connectedSources.has(handleId)) continue;
        const offsetY = handleId === 'true' ? -30 : 30;
        const phId = `_ph_${node.id}_${handleId}`;
        phNodes.push({
          id: phId,
          type: 'placeholderNode',
          position: {
            x: node.position.x + 160 + PLACEHOLDER_OFFSET_X,
            y: node.position.y + offsetY,
          },
          data: { onClick: () => handlePlaceholderClick(node.id, handleId) },
          selectable: false,
          draggable: false,
          focusable: false,
        });
        phEdges.push({
          id: `_phe_${node.id}_${handleId}`,
          source: node.id,
          sourceHandle: handleId,
          target: phId,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#e5e7eb', strokeWidth: 1, strokeDasharray: '6 4' },
        });
      }
    } else {
      if (!connectedSources.has('')) {
        const phId = `_ph_${node.id}`;
        phNodes.push({
          id: phId,
          type: 'placeholderNode',
          position: {
            x: node.position.x + 160 + PLACEHOLDER_OFFSET_X,
            y: node.position.y + 3,
          },
          data: { onClick: () => handlePlaceholderClick(node.id, null) },
          selectable: false,
          draggable: false,
          focusable: false,
        });
        phEdges.push({
          id: `_phe_${node.id}`,
          source: node.id,
          target: phId,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#e5e7eb', strokeWidth: 1, strokeDasharray: '6 4' },
        });
      }
    }

    return { placeholderNodes: phNodes, placeholderEdges: phEdges };
  }, [selectedNodeId, nodes, edges, nodeConfigs, handlePlaceholderClick]);

  // Pan-to-node API for callers (e.g. Explain panel "view step")
  // is not exposed here directly; the parent reads node positions from
  // state and panning is initiated through reactFlowInstance via the
  // selectNode flow. To keep panning available across views, the parent
  // exposes a separate `onHighlightNode` that lives outside this view.

  return (
    <div
      className="flex-1 relative"
      ref={reactFlowWrapper}
      onContextMenu={handleCanvasContextMenu}
    >
      <ReactFlow
        nodes={[...nodesWithStatus, ...placeholderNodes]}
        edges={[...edges, ...placeholderEdges]}
        onNodesChange={(changes) => {
          const filtered = changes.filter((c) => !c.id?.startsWith('_ph_'));
          if (filtered.length > 0) onNodesChange(filtered);
        }}
        onEdgesChange={(changes) => {
          const filtered = changes.filter((c) => !c.id?.startsWith('_phe_'));
          if (filtered.length > 0) onEdgesChange(filtered);
        }}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onInit={setReactFlowInstance}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        deleteKeyCode={['Backspace', 'Delete']}
      >
        <Background variant="dots" gap={20} size={0.5} color="#d4d4d4" className="!bg-[#fafafa]" />
        <Controls showInteractive={false} className="!border-gray-200 !rounded-lg !shadow-none" />
        <MiniMap
          nodeColor={(node) => {
            if (node.id.startsWith('_ph_')) return '#e5e7eb';
            return '#9ca3af';
          }}
          maskColor="rgba(0,0,0,0.03)"
          style={{ border: '1px solid #f3f4f6', borderRadius: 12 }}
        />
      </ReactFlow>

      {nodes.length === 0 && !contextMenu && (
        <EmptyCanvasOverlay
          onAddTemplate={handleAddTemplate}
          onAddTrigger={handleAddSingleTrigger}
        />
      )}

      {contextMenu && (
        <CanvasContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onAddNode={handleContextMenuAddNode}
          onClose={() => setContextMenu(null)}
          connectFrom={contextMenu.connectFrom || null}
        />
      )}
    </div>
  );
}
